import { Box, Text } from '@chakra-ui/react'
import {
  AudioOnlyReason,
  audioOnlyReasonChartText,
  visitLocationField,
  VisitModality,
} from '@hb/shared'
import { useEffect, useRef, useState } from 'react'
import { useField } from 'react-final-form'
import { FormElement } from '../../../../components/forms'
import { SectionLabel } from '../../SectionLabel'
import {
  TelehealthAcknowledgmentModal,
  useTelehealthAcknowledgment,
} from '../TelehealthAcknowledgmentModal'

const AudioOnlyChartText = () => {
  const {
    input: { value: reason },
  } = useField<AudioOnlyReason | undefined>('data.visitLocation.audioOnlyReason', {
    subscription: { value: true },
  })

  if (!reason || reason === 'other') return null

  return (
    <Box
      mt={2}
      mb={1}
      px={3}
      py={2}
      bg="blue.50"
      borderLeft="3px solid"
      borderColor="blue.400"
      borderRadius={4}>
      <Text fontSize="sm" color="blue.700" fontStyle="italic">
        {audioOnlyReasonChartText[reason]}
      </Text>
    </Box>
  )
}

const AudioOnlyAuditWarning = () => {
  const {
    input: { value: reason },
  } = useField<AudioOnlyReason | undefined>('data.visitLocation.audioOnlyReason', {
    subscription: { value: true },
  })

  if (reason !== 'other') return null

  return (
    <Box
      mt={2}
      mb={1}
      px={3}
      py={2}
      bg="orange.50"
      borderLeft="3px solid"
      borderColor="orange.400"
      borderRadius={4}>
      <Text fontSize="sm" color="orange.700" fontWeight={600}>
        Note: Free-text audio-only justifications carry higher audit risk.
      </Text>
    </Box>
  )
}

// §2.1/§2.10: Tier 2 notation banner for Routine + Telehealth.
const TelehealthTierNotice = () => (
  <Box
    mt={2}
    px={3}
    py={2}
    bg="yellow.50"
    _dark={{ bg: 'yellow.900' }}
    borderLeft="3px solid"
    borderColor="yellow.400"
    borderRadius={4}>
    <Text fontSize="sm" color="yellow.800" _dark={{ color: 'yellow.200' }} fontWeight={500}>
      Telehealth visit — fields requiring physical presence (fundal height, FHT, weight, BP) are
      Tier 2 for this visit and will not block authentication if undocumented.
    </Text>
  </Box>
)

const isTelehealth = (modality: VisitModality | undefined) =>
  modality === 'telehealth-video' || modality === 'telehealth-audio-only'

export const VisitLocationInput = ({ isRoutineAntepartum }: { isRoutineAntepartum?: boolean }) => {
  const {
    input: { value: modality },
  } = useField<VisitModality | undefined>('data.visitLocation.modality', {
    subscription: { value: true },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const acknowledgedRef = useRef(false)
  const { handleConfirm, handleDoNotBill, handleCancel } = useTelehealthAcknowledgment()

  // §2.1: Show acknowledgment modal when routine antepartum + telehealth modality is selected.
  useEffect(() => {
    if (isRoutineAntepartum && isTelehealth(modality) && !acknowledgedRef.current) {
      setModalOpen(true)
    }
    if (!isTelehealth(modality)) {
      acknowledgedRef.current = false
    }
  }, [isRoutineAntepartum, modality])

  const onConfirm = () => {
    acknowledgedRef.current = true
    setModalOpen(false)
    handleConfirm()
  }

  const onDoNotBill = () => {
    acknowledgedRef.current = true
    setModalOpen(false)
    handleDoNotBill()
  }

  const onCancel = () => {
    setModalOpen(false)
    handleCancel()
  }

  return (
    <Box px={5} py="14px" w="100%">
      <SectionLabel mb={1}>Visit Location</SectionLabel>
      <FormElement root showName={false} field={visitLocationField} name="data.visitLocation" />
      <AudioOnlyChartText />
      <AudioOnlyAuditWarning />
      {isRoutineAntepartum && isTelehealth(modality) ? <TelehealthTierNotice /> : null}
      <TelehealthAcknowledgmentModal
        isOpen={modalOpen}
        onConfirm={onConfirm}
        onDoNotBill={onDoNotBill}
        onCancel={onCancel}
      />
    </Box>
  )
}
